// The navigation query's cache, for writes made outside a component. A client
// user's persona lives on the server, so a write to it updates this cache
// rather than the query key.

import { queryClient } from '@/utils/queryClient';
import type { NavigationResponse } from '@dashai/lib/navigationClient';

export const NAVIGATION_QUERY_KEY = 'navigation';

const keyFor = (uid: string, workspace: string) => [NAVIGATION_QUERY_KEY, uid, workspace];

// Refetch the user's navigation in a workspace after a write to their persona.
export function refreshNavigation(uid: string, workspace: string): void {
  void queryClient.invalidateQueries({ queryKey: keyFor(uid, workspace) });
}

// Apply an "Add to home" toggle to the cached navigation before the server
// confirms it. The refresh that follows the write reconciles.
export function toggleCachedExtra(
  uid: string,
  workspace: string,
  kind: 'dashboards' | 'workflows',
  id: string,
  on: boolean,
): void {
  queryClient.setQueriesData<NavigationResponse>({ queryKey: keyFor(uid, workspace) }, (nav) =>
    nav ? withExtraToggled(nav, kind, id, on) : nav,
  );
}

// "Add to home" moves an entry into the persona set before the server confirms
// it, and the refetch that follows reconciles. Removing one moves it back.
export function withExtraToggled(
  navigation: NavigationResponse,
  kind: 'dashboards' | 'workflows',
  id: string,
  on: boolean,
): NavigationResponse {
  const split = navigation[kind];
  const added = navigation.user_added[kind].filter((x) => x !== id);
  if (on && split.not_in_persona.includes(id)) {
    return {
      ...navigation,
      [kind]: {
        in_persona: [...split.in_persona, id],
        not_in_persona: split.not_in_persona.filter((x) => x !== id),
      },
      user_added: { ...navigation.user_added, [kind]: [...added, id] },
    };
  }
  if (!on && navigation.user_added[kind].includes(id)) {
    return {
      ...navigation,
      [kind]: {
        in_persona: split.in_persona.filter((x) => x !== id),
        not_in_persona: [...split.not_in_persona, id],
      },
      user_added: { ...navigation.user_added, [kind]: added },
    };
  }
  return navigation;
}
